<template>
  <div class="game-cards-view">
    <div class="cards-header-section">
      <h1>Card Collection</h1>
      <p class="subtitle">Open packs to add cards to your collection.</p>
    </div>

    <section class="packs-section">
      <h2>Unopened Packs</h2>
      <p v-if="unopenedPacks.length === 0" class="empty-msg">
        You have no unopened packs.
      </p>
      <div v-else ref="packListEl" class="pack-list">
        <article
          v-for="pack in unopenedPacks"
          :key="pack.id"
          class="pack-tile"
          :data-pack-id="pack.id"
        >
          <img
            class="pack-tile-back"
            src="/assets/cards/back_playing_card_2.png"
            alt=""
          />
          <div class="pack-tile-overlay">
            <h3>{{ pack.name }}</h3>
            <p>{{ pack.description }}</p>
            <button
              type="button"
              class="open-btn"
              :disabled="openingId === pack.id"
              @click="handleOpenPack(pack.id)"
            >
              {{ openingId === pack.id ? "Opening…" : "Open" }}
            </button>
          </div>
        </article>
      </div>
    </section>

    <section class="collection-section">
      <h2>Your Collection</h2>
      <div v-if="gameStore.ownedCards.length > 0" class="filter-bar">
        <input
          v-model="searchText"
          class="filter-search"
          type="search"
          placeholder="Search name or text…"
        />
        <select v-model="rarityFilter">
          <option value="">All rarities</option>
          <option v-for="rarity in rarityOptions" :key="rarity" :value="rarity">
            {{ rarity }}
          </option>
        </select>
        <select v-model="typeFilter">
          <option value="">All types</option>
          <option v-for="type in typeOptions" :key="type" :value="type">
            {{ type }}
          </option>
        </select>
        <select v-model="setFilter">
          <option value="">All sets</option>
          <option v-for="setName in setOptions" :key="setName" :value="setName">
            {{ setName }}
          </option>
        </select>
        <select v-model="subTypeFilter">
          <option value="">All subtypes</option>
          <option v-for="subType in subTypeOptions" :key="subType" :value="subType">
            {{ subType }}
          </option>
        </select>
      </div>
      <div v-if="loading" class="loading-state">Loading collection...</div>
      <p v-else-if="gameStore.ownedCards.length === 0" class="empty-msg">
        Open a pack to start your collection.
      </p>
      <p v-else-if="filteredOwnedCards.length === 0" class="empty-msg">
        No cards match these filters.
      </p>
      <div v-else class="card-gallery">
        <button
          v-for="card in filteredOwnedCards"
          :key="card.id"
          type="button"
          class="gallery-card"
          :class="{ selected: selectedCard?.id === card.id }"
          @click="toggleSelectedCard(card)"
        >
          <PlayingCard :card="card" :face-up="true" />
        </button>
      </div>
    </section>

    <div
      v-if="selectedCard"
      class="drawer-backdrop"
      @click.self="closeDrawer"
    ></div>
    <aside
      v-if="selectedCard"
      class="card-drawer"
      :class="{ 'materialize-anim': shouldAnimate }"
      @animationend="handleAnimationEnd"
    >
      <div v-if="shouldAnimate" class="dust-particles">
        <div
          class="particle"
          v-for="n in 20"
          :key="n"
          :style="getParticleStyle(n)"
        ></div>
      </div>
      <div class="card-drawer-panel glass-panel">
        <header class="drawer-header">
          <h2>{{ selectedCard.name }}</h2>
          <button
            type="button"
            class="drawer-close"
            title="Close"
            @click="closeDrawer"
          >
            <i class="fas fa-times"></i>
          </button>
        </header>
        <div class="drawer-card-wrap">
          <PlayingCard
            class="drawer-card"
            :card="selectedCard"
            :face-up="drawerFaceUp"
            :hide-overlay="hideOverlay"
          />
          <p
            v-if="hasFullArtOverlay(selectedCard) && selectedCard.legend"
            class="legend-plaque"
          >
            {{ selectedCard.legend }}
          </p>
        </div>
        <div class="drawer-actions">
          <button
            type="button"
            class="drawer-icon-btn"
            title="Flip Card"
            @click="drawerFaceUp = !drawerFaceUp"
          >
            <i class="fas fa-sync-alt"></i>
          </button>
          <button
            v-if="hasFullArtOverlay(selectedCard)"
            type="button"
            class="drawer-icon-btn"
            title="Toggle Overlay"
            @click="hideOverlay = !hideOverlay"
          >
            <i :class="hideOverlay ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
          </button>
          <button
            type="button"
            class="drawer-icon-btn sell-btn"
            :disabled="isSelectedInUse || isSelectedUntradeable || listing"
            :title="
              isSelectedInUse
                ? 'Cannot list a socketed card'
                : isSelectedUntradeable
                  ? 'Starter buildings cannot be traded'
                  : 'Sell on Market'
            "
            @click="handleSellCard"
          >
            <i class="fas fa-coins"></i>
          </button>
          <button
            type="button"
            class="drawer-icon-btn destroy-btn"
            :disabled="isSelectedInUse || destroying"
            :title="
              isSelectedInUse
                ? 'Cannot destroy a socketed card'
                : `Destroy for ${selectedEssencePayout} Essence`
            "
            @click="handleDestroyCard"
          >
            <i class="fas fa-bolt"></i>
          </button>
        </div>
      </div>
    </aside>

    <Modal
      :is-visible="destroyModalVisible"
      title="Destroy Card"
      :message="destroyModalMessage"
      @confirm="confirmDestroyCard"
      @cancel="destroyModalVisible = false"
    />

    <Modal
      :is-visible="sellModalVisible"
      title="Sell on Market"
      confirm-label="List Card"
      @confirm="confirmSellCard"
      @cancel="sellModalVisible = false"
    >
      <div class="sell-modal-body">
        <p>
          List {{ selectedCard?.name || "this card" }} for gold. A 2% listing fee
          is charged now and is not refunded.
        </p>
        <label class="sell-price-label">
          Asking price (gold)
          <input
            v-model.number="sellPrice"
            class="sell-price-input"
            type="number"
            min="1"
            step="1"
          />
        </label>
        <p class="sell-fee-line">
          Listing fee: {{ listingFeeAmount }} gold
          <span v-if="!canAffordListingFee"> — not enough gold</span>
        </p>
      </div>
    </Modal>

    <PackRevealModal
      :open="revealOpen"
      :cards="revealedCards"
      @close="closeReveal"
    />
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { gsap } from "gsap";
import { useGameStore } from "../store/game";
import { useUiStore } from "../store/ui";
import packsData from "@/data/packs.json";
import racesData from "@/data/races.json";
import { snapshotToBaseStats } from "../utils/cardStats";
import PlayingCard from "../components/PlayingCard.vue";
import PackRevealModal from "../components/PackRevealModal.vue";
import Modal from "../components/Modal.vue";
import { useMarketStore } from "../store/market";
import { listingFee } from "../utils/cardMarketFees";

const gameStore = useGameStore();
const uiStore = useUiStore();
const marketStore = useMarketStore();
const loading = ref(false);
const openingId = ref(null);
const revealOpen = ref(false);
const revealedCards = ref([]);
const searchText = ref("");
const rarityFilter = ref("");
const typeFilter = ref("");
const setFilter = ref("");
const subTypeFilter = ref("");
const packListEl = ref(null);
const selectedCard = ref(null);
const drawerFaceUp = ref(true);
const hideOverlay = ref(false);
const destroying = ref(false);
const destroyModalVisible = ref(false);
const sellModalVisible = ref(false);
const sellPrice = ref(50);
const listing = ref(false);
const shouldAnimate = ref(false);

const ESSENCE_BY_RARITY = {
  common: 5,
  uncommon: 10,
  rare: 15,
  epic: 20,
  legendary: 30,
};

function essencePayoutForRarity(rarity) {
  const key = String(rarity || "").toLowerCase();
  return ESSENCE_BY_RARITY[key] || 5;
}

function hasFullArtOverlay(card) {
  return card?.type === "hero" || card?.type === "equipment";
}

const packCatalog = computed(() => (Array.isArray(packsData) ? packsData : []));

const packBySlug = computed(() => {
  const map = {};
  for (const pack of packCatalog.value) {
    map[pack.slug] = pack;
  }
  return map;
});

const unopenedPacks = computed(() =>
  gameStore.playerPacks.map((pack) => {
    const def = packBySlug.value[pack.pack_slug] || {};
    return {
      id: pack.id,
      pack_slug: pack.pack_slug,
      name: def.name || pack.pack_slug,
      description: def.description || "",
      image: def.image || "",
    };
  })
);

const uniqueValues = (key) =>
  [...new Set(gameStore.ownedCards.map((card) => card[key]).filter(Boolean))].sort();

const rarityOptions = computed(() => uniqueValues("rarity"));
const typeOptions = computed(() => uniqueValues("type"));
const setOptions = computed(() => uniqueValues("set"));
const subTypeOptions = computed(() => uniqueValues("sub_type"));

const filteredOwnedCards = computed(() => {
  const q = searchText.value.trim().toLowerCase();
  return gameStore.ownedCards.filter((card) => {
    if (rarityFilter.value && card.rarity !== rarityFilter.value) return false;
    if (typeFilter.value && card.type !== typeFilter.value) return false;
    if (setFilter.value && card.set !== setFilter.value) return false;
    if (subTypeFilter.value && card.sub_type !== subTypeFilter.value) return false;
    if (!q) return true;
    return (
      card.name.toLowerCase().includes(q) ||
      card.description.toLowerCase().includes(q) ||
      card.legend.toLowerCase().includes(q)
    );
  });
});

const isSelectedInUse = computed(
  () => selectedCard.value?.instance_data?.in_use === true
);

const starterSlugs = new Set(
  (Array.isArray(racesData) ? racesData : []).flatMap(
    (race) => race?.starter_pack?.cards || []
  )
);

const isSelectedUntradeable = computed(() => {
  const card = selectedCard.value;
  if (!card) return false;
  if (card.instance_data?.untradeable === true) return true;
  return starterSlugs.has(card.slug);
});

const selectedEssencePayout = computed(() =>
  essencePayoutForRarity(selectedCard.value?.rarity)
);

const listingFeeAmount = computed(() =>
  listingFee(Number.parseInt(sellPrice.value, 10) || 0)
);

const playerGold = computed(
  () => Number(gameStore.spendableResources?.gold) || 0
);

const canAffordListingFee = computed(
  () => playerGold.value >= listingFeeAmount.value
);

const destroyModalMessage = computed(() => {
  const name = selectedCard.value?.name || "this card";
  return `Destroy ${name} for ${selectedEssencePayout.value} Essence? This cannot be undone.`;
});

function closeDrawer() {
  shouldAnimate.value = false;
  destroyModalVisible.value = false;
  sellModalVisible.value = false;
  selectedCard.value = null;
  drawerFaceUp.value = true;
  hideOverlay.value = false;
}

function toggleSelectedCard(card) {
  if (selectedCard.value?.id === card.id) {
    closeDrawer();
    return;
  }
  selectedCard.value = card;
  drawerFaceUp.value = true;
  hideOverlay.value = false;
  if (card?.instance_data?.is_new) {
    gameStore.acknowledgeNewCard(card.id).then(() => {
      const updated = gameStore.ownedCards.find(
        (owned) => Number(owned.id) === Number(card.id)
      );
      if (updated && selectedCard.value?.id === card.id) {
        selectedCard.value = updated;
      }
    });
  }
}

watch(
  selectedCard,
  (card, prev) => {
    uiStore.setSidePanelOpen(!!card);
    if (!card) {
      shouldAnimate.value = false;
      return;
    }
    if (!prev) {
      shouldAnimate.value = true;
    }
  },
  { immediate: true }
);

function handleAnimationEnd() {
  shouldAnimate.value = false;
}

const MathRand = (min, max) => Math.random() * (max - min) + min;
const getParticleStyle = () => {
  return {
    left: `${MathRand(0, 100)}%`,
    top: `${MathRand(0, 100)}%`,
    animationDelay: `${MathRand(0, 0.5)}s`,
    animationDuration: `${MathRand(1, 2)}s`,
    transform: `scale(${MathRand(0.5, 1.5)})`,
  };
};

function handleDestroyCard() {
  if (!selectedCard.value || isSelectedInUse.value || destroying.value) return;
  destroyModalVisible.value = true;
}

function handleSellCard() {
  if (
    !selectedCard.value ||
    isSelectedInUse.value ||
    isSelectedUntradeable.value ||
    listing.value
  ) {
    return;
  }
  sellPrice.value = 50;
  sellModalVisible.value = true;
}

async function confirmSellCard() {
  if (
    !selectedCard.value ||
    isSelectedInUse.value ||
    isSelectedUntradeable.value ||
    listing.value
  ) {
    return;
  }
  const price = Number.parseInt(sellPrice.value, 10);
  if (!Number.isInteger(price) || price <= 0) return;
  if (!canAffordListingFee.value) return;
  listing.value = true;
  try {
    await marketStore.listCard({
      playerCardId: selectedCard.value.id,
      price,
    });
    sellModalVisible.value = false;
    closeDrawer();
  } catch (error) {
    alert(
      error.response?.data?.message ||
        error.message ||
        "Failed to list card"
    );
  } finally {
    listing.value = false;
  }
}

async function confirmDestroyCard() {
  if (!selectedCard.value || isSelectedInUse.value || destroying.value) return;
  destroyModalVisible.value = false;
  destroying.value = true;
  try {
    await gameStore.destroyCard(selectedCard.value.id);
    closeDrawer();
  } catch (error) {
    alert(error.response?.data?.message || error.message || "Failed to destroy card");
  } finally {
    destroying.value = false;
  }
}

function animatePackArrival() {
  const tiles = packListEl.value?.querySelectorAll(".pack-tile");
  if (!tiles?.length) return;
  gsap.fromTo(
    tiles,
    { opacity: 0, scale: 0.7, y: 24 },
    {
      opacity: 1,
      scale: 1,
      y: 0,
      duration: 0.55,
      stagger: 0.12,
      ease: "back.out(1.6)",
    }
  );
}

watch(
  () => unopenedPacks.value.length,
  async (count) => {
    if (count > 0) {
      await nextTick();
      animatePackArrival();
    }
  }
);

onMounted(async () => {
  loading.value = true;
  try {
    await Promise.all([
      gameStore.fetchPlayerPacks(),
      gameStore.fetchPlayerCards(),
    ]);
  } finally {
    loading.value = false;
    await nextTick();
    animatePackArrival();
  }
});

onUnmounted(() => {
  shouldAnimate.value = false;
  uiStore.setSidePanelOpen(false);
});

async function handleOpenPack(packId) {
  openingId.value = packId;
  try {
    const granted = await gameStore.openPack(packId);
    revealedCards.value = granted.map((row, index) => {
      if (typeof row === "string") {
        const owned = gameStore.ownedCards.find((card) => card.slug === row);
        return {
          ...owned,
          slug: row,
          name: owned?.name || row,
          rarity: owned?.rarity || "common",
          type: owned?.type || "",
          image: owned?.image || owned?.image || "",
          stats: owned?.stats || {},
          abilities: owned?.abilities || {},
          base_stats: owned?.base_stats || {},
          instance_data: owned?.instance_data || {},
        };
      }
      const owned = gameStore.ownedCards.find((card) => card.id === row.id);
      const instance = row.instance_data || owned?.instance_data || {};
      return {
        ...owned,
        id: row.id || `reveal-${index}`,
        slug: row.card_slug || owned?.slug,
        name: owned?.name || row.card_slug,
        rarity: instance.rarity || owned?.rarity || "common",
        type: owned?.type || "",
        image: owned?.image || owned?.image || "",
        stats: instance.stats || owned?.stats || {},
        affixes: instance.affixes || owned?.affixes || [],
        abilities: owned?.abilities || {},
        base_stats: snapshotToBaseStats(instance),
        instance_data: instance,
      };
    });
    revealOpen.value = true;
  } catch (error) {
    alert(error.response?.data?.message || error.message || "Failed to open pack");
  } finally {
    openingId.value = null;
  }
}

function closeReveal() {
  revealOpen.value = false;
  revealedCards.value = [];
}
</script>

<style scoped>
.game-cards-view {
  padding: 1rem;
  max-width: 1100px;
  margin: 0 auto;
  color: #fff;
  padding-bottom: 80px;
}

.cards-header-section {
  text-align: center;
  margin-bottom: 2rem;
}

h1 {
  font-family: "Cinzel", serif;
  font-size: 2rem;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-shadow: 0 0 10px rgba(0, 240, 255, 0.5);
}

h2 {
  font-family: "Cinzel", serif;
  font-size: 1.2rem;
  margin: 0 0 1rem;
  letter-spacing: 1px;
  color: #88c0d0;
}

.subtitle {
  color: #88c0d0;
  font-size: 0.9rem;
}

.packs-section,
.collection-section {
  margin-bottom: 2.5rem;
}

.filter-bar {
  display: grid;
  grid-template-columns: 1.4fr repeat(4, minmax(110px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.filter-search,
.filter-bar select {
  background: rgba(10, 16, 24, 0.9);
  border: 1px solid #3d5a73;
  border-radius: 6px;
  color: #e7ecf1;
  padding: 8px 10px;
  font: inherit;
}

.loading-state {
  text-align: center;
  padding: 2rem;
  color: #00f0ff;
  font-style: italic;
}

.empty-msg {
  text-align: center;
  color: #555;
  margin-top: 1rem;
  font-style: italic;
}

.pack-list,
.card-gallery,
.card-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.25rem;
}

.gallery-card {
  display: block;
  width: 100%;
  padding: 0;
  margin: 0;
  border: 2px solid transparent;
  border-radius: 14px;
  background: transparent;
  cursor: pointer;
  color: inherit;
  font: inherit;
  text-align: left;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.gallery-card:hover {
  border-color: rgba(0, 246, 255, 0.45);
}

.gallery-card.selected {
  border-color: #00f6ff;
  box-shadow: 0 0 14px rgba(0, 246, 255, 0.4);
}

.pack-tile {
  position: relative;
  aspect-ratio: 5 / 7;
  border: 2px solid #3d5a73;
  border-radius: 12px;
  overflow: hidden;
  background: #121820;
}

.pack-tile-back {
  position: absolute;
  inset: 0;
  z-index: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pack-tile-overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  height: 40%;
  padding: 0.5rem 0.6rem 0.65rem;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  flex-direction: column;
  gap: 0.28rem;
  overflow: hidden;
}

.pack-tile-overlay h3 {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.2;
  color: #fff;
  font-family: inherit;
  letter-spacing: 0;
}

.pack-tile-overlay p {
  margin: 0;
  flex: 1;
  min-height: 0;
  color: #c8d4de;
  font-size: 0.72rem;
  line-height: 1.3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.pack-tile-overlay .open-btn {
  position: relative;
  z-index: 3;
  flex-shrink: 0;
  align-self: stretch;
}

.card-tile {
  background: rgba(20, 30, 45, 0.85);
  border: 1px solid #3d5a73;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.card-tile[data-rarity="uncommon"] {
  border-color: #3d9e6e;
}
.card-tile[data-rarity="rare"] {
  border-color: #3d7a9e;
}
.card-tile[data-rarity="epic"] {
  border-color: #8a4dff;
}
.card-tile[data-rarity="legendary"] {
  border-color: #ffcc66;
  box-shadow: 0 0 14px rgba(255, 204, 102, 0.35);
}

.card-tile-image {
  height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(10, 16, 24, 0.8);
}

.card-tile-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.card-tile-image.placeholder {
  color: #3d7a9e;
  font-size: 2.5rem;
}

.card-tile-body {
  padding: 1rem;
}

.card-tile-body h2 {
  margin: 0 0 0.4rem;
  font-size: 1.1rem;
  color: #e7ecf1;
  font-family: inherit;
  letter-spacing: 0;
}

.card-description {
  margin: 0 0 0.8rem;
  color: #aab8c5;
  font-size: 0.9rem;
  line-height: 1.4;
}

.card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin: 0 0 0.6rem;
  color: #88c0d0;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.card-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 0.75rem;
  margin: 0 0 0.6rem;
  color: #73daca;
  font-size: 0.8rem;
}

.open-btn {
  background: #1b6b4a;
  border: 1px solid #2a9d6e;
  color: #e7ecf1;
  border-radius: 6px;
  padding: 8px 14px;
  cursor: pointer;
  font: inherit;
}

.open-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 800px) {
  .filter-bar {
    grid-template-columns: 1fr;
  }
}

.materialize-anim {
  animation: panel-materialize 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  opacity: 0;
}

@keyframes panel-materialize {
  0% {
    opacity: 0;
    transform: translateX(50px) scale(0.95);
    filter: blur(10px) brightness(2);
  }
  50% {
    opacity: 0.8;
    filter: blur(2px) brightness(1.5);
  }
  100% {
    opacity: 1;
    transform: translateX(0) scale(1);
    filter: blur(0) brightness(1);
  }
}

.dust-particles {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 900;
  overflow: hidden;
}

.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background-color: #00f6ff;
  border-radius: 50%;
  box-shadow: 0 0 8px #00f6ff, 0 0 12px #00f6ff;
  opacity: 0;
  animation: particle-fade-float 2s ease-out forwards;
}

@keyframes particle-fade-float {
  0% {
    opacity: 0;
    transform: translateY(20px) scale(1);
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translateY(-40px) scale(0);
  }
}

.card-drawer {
  position: fixed;
  right: 0;
  top: 70px;
  bottom: 80px;
  width: 350px;
  z-index: 800;
  display: flex;
  flex-direction: column;
}

.glass-panel {
  flex-grow: 1;
  background-color: rgba(20, 20, 30, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-left: 1px solid rgba(0, 246, 255, 0.3);
  box-shadow: -5px 0 20px rgba(0, 0, 0, 0.5), inset 0 0 20px rgba(0, 246, 255, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid rgba(0, 246, 255, 0.2);
  background: rgba(36, 40, 59, 0.6);
}

.drawer-header h2 {
  margin: 0;
  font-size: 1rem;
  color: #00f6ff;
}

.drawer-close {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  margin: 0;
  line-height: 1;
  background: none;
  border: 1px solid rgba(0, 246, 255, 0.3);
  color: #a0a0b0;
  border-radius: 6px;
  cursor: pointer;
}

.drawer-close i {
  line-height: 1;
}

.drawer-close:hover {
  color: #00f6ff;
  border-color: #00f6ff;
}

.drawer-card-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  min-height: 0;
  overflow-y: auto;
}

.drawer-card {
  width: 100%;
  max-width: 100%;
  flex-shrink: 0;
}

.legend-plaque {
  margin: 0;
  padding: 0.75rem 0.9rem;
  font-family: "Cinzel", "Palatino Linotype", serif;
  font-size: 0.95rem;
  font-style: italic;
  font-weight: 500;
  letter-spacing: 0.03em;
  line-height: 1.5;
  color: #f8e6b0;
  background: rgba(10, 10, 20, 0.45);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border: 1px solid rgba(212, 175, 55, 0.35);
  border-radius: 6px;
  text-shadow: 0 0 10px rgba(212, 175, 55, 0.45), 0 1px 2px rgba(0, 0, 0, 0.7);
}

.drawer-actions {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  padding: 0 1rem 1.1rem;
}

.drawer-icon-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 42px;
  height: 42px;
  padding: 0;
  background: rgba(0, 246, 255, 0.08);
  border: 1px solid rgba(0, 246, 255, 0.45);
  color: #00f6ff;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  box-shadow: 0 0 8px rgba(0, 246, 255, 0.12);
  transition: all 0.2s ease;
}

.drawer-icon-btn:hover:not(:disabled) {
  transform: scale(1.08);
  background: rgba(0, 246, 255, 0.18);
  box-shadow: 0 0 16px rgba(0, 246, 255, 0.45);
}

.drawer-icon-btn:active:not(:disabled) {
  transform: scale(0.96);
}

.drawer-icon-btn.destroy-btn {
  border-color: rgba(224, 90, 90, 0.65);
  color: #e06a6a;
  box-shadow: 0 0 8px rgba(180, 30, 30, 0.2);
}

.drawer-icon-btn.destroy-btn:hover:not(:disabled) {
  background: rgba(180, 30, 30, 0.25);
  box-shadow: 0 0 16px rgba(224, 90, 90, 0.5);
}

.drawer-icon-btn.sell-btn {
  border-color: rgba(232, 213, 163, 0.65);
  color: #e8d5a3;
  box-shadow: 0 0 8px rgba(232, 213, 163, 0.18);
}

.drawer-icon-btn.sell-btn:hover:not(:disabled) {
  background: rgba(232, 213, 163, 0.18);
  box-shadow: 0 0 16px rgba(232, 213, 163, 0.45);
}

.sell-modal-body p {
  margin: 0 0 0.75rem;
}

.sell-price-label {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.82rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #c8b56a;
}

.sell-price-input {
  width: 8rem;
  background: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(232, 213, 163, 0.55);
  color: #fff8e4;
  font-size: 1rem;
  padding: 0.35rem 0.4rem;
}

.sell-fee-line {
  margin-top: 0.7rem;
  color: #d8d2c2;
  font-size: 0.9rem;
}

.drawer-icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.drawer-backdrop {
  display: none;
}

@media (max-width: 768px) {
  .drawer-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
    z-index: 1190;
  }

  .card-drawer {
    top: 0;
    right: 0;
    bottom: 0;
    width: 86vw;
    max-width: 380px;
    height: 100dvh;
    z-index: 1200;
    box-shadow: -8px 0 24px rgba(0, 0, 0, 0.7);
    transform: translateX(0);
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .materialize-anim {
    animation: none;
    opacity: 1;
  }

  .glass-panel {
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }
}
</style>
